import * as Sentry from '@sentry/browser';
import { getBidderEmail, getBidderId, getUserData, isUserLoggedIn, userDataSelector } from '@/redux/modules/user';
import createSentryMiddleware from 'redux-sentry-middleware';

const ignoreErrors = [
    // Random plugins/extensions
    'top.GLOBALS', // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    'fb_xd_fragment',
    'fb_getSelectionScriptMessageHandle',
    'focusHelper', // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived', // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage', // Try to disable some errors to get the counts down
    'boomerang-1.0.0',
    'NetworkError when attempting to fetch resource.',
    'Pubnub service failed to respond to time request',
    'instream/video/client',
    'NS_ERROR_NOT_INITIALIZED',
    'Dropped error due to throttling!',
    'getRandomIntInclusive',
    'CustomEvent',
    "evaluating 'this.element.removeEventListener'",
    "evaluating 'screen.orientation.angle'",
    "Failed to execute 'removeChild' on 'Node'",
    'Cannot declare a const variable twice',
    'BetterJsPop',
    'RISKX',
    'get_url_string_from_object',
    'RISKX.keys',
    'react-onclickoutside',
    'Object.RISKX.get_url_string_from_object',
    'RISKX.get_url_string_from_object',
    'Object.RISKX.clientInfoToString',
    'Object.RISKX.sendRequest',
    'RISKX.onClientInfo',
    "undefined is not an object (evaluating 'webkit.messageHandlers.readerModeMessageHandler')",
    'webkit.messageHandlers.readerModeMessageHandler',
    "undefined is not an object (evaluating 'webkit.messageHandlers.trackingProtectionStats')",
    'webkit.messageHandlers.trackingProtectionStats',
    'Loading chunk',
];

type InstallSentryParams = {
    dsn: string;
    environment?: string;
    release?: string;
    sampleRate?: number;
};
export const installSentry = ({
    dsn,
    environment = 'preprod',
    release = 'LOCALDEV',
    sampleRate = 0.1,
}: InstallSentryParams) => {
    Sentry.init({
        // @ts-ignore
        captureUnhandledRejections: false,
        debug: release === 'LOCALDEV',
        dsn,
        environment,
        ignoreErrors,
        ignoreUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
        release,
        sampleRate,
        sanitizeKeys: [/token/, /password/i],
    });
};

export const captureError = (error: any, errorInfo: any) => {
    Sentry.withScope((scope) => {
        // @ts-ignore
        scope.setExtra(errorInfo);
        Sentry.captureException(error);
    });
};

export const createMiddleware = () =>
    createSentryMiddleware(Sentry, {
        actionTransformer,
        breadcrumbDataFromAction,
        getTags,
        getUserContext,
        stateTransformer,
    });

const breadcrumbDataFromAction = ({ payload, type }) => {
    if (type.includes('REQUEST')) {
        return { payload: JSON.stringify(payload) };
    }
};

const actionTransformer = ({ payload, type }) => {
    if (type.includes('REQUEST')) {
        return { payload, type };
    }

    return { type };
};

const getTags = (state) => {
    const tags: any = {
        errorType: 'Client Side 500',
    };

    if (isUserLoggedIn(state)) {
        tags['Logged In'] = true;
        tags['High NMS'] = (getUserData(state) || {}).ANMS >= 5000;
    } else {
        tags['Logged In'] = false;
    }

    // @ts-ignore
    if (window.FS && window.FS.getCurrentSessionURL) {
        // @ts-ignore
        tags['FullStory'] = window.FS.getCurrentSessionURL();
    }

    return tags;
};

const getUserContext = (state) => ({
    bidderId: getBidderId(state),
    email: getBidderEmail(state),
});

const stateTransformer = (state) => {
    const userData = userDataSelector(state);
    if (isUserLoggedIn(state) && userData) {
        return {
            ...userData,
            address: 'REDACTED',
            address2: 'REDACTED',
            mobileNumber: 'REDACTED',
            phoneNumber: 'REDACTED',
        };
    }

    return {};
};
